// Where a signed-in session is kept: `localStorage` for an ordinary one (survives restart),
// `sessionStorage` for a public-computer one (dropped on browser close).

import { USER_STATE_STORAGE_KEY } from './constants';
import { isPublicComputerUser } from './public-computer-claim';

/** Session-material keys that move together on isolation (redirect keys are written pre-claim). */
const SESSION_KEYS = ['UserState', 'userId', 'redirectQueryParams', 'redirectPath', 'postLogInRedirect'];
/** oidc-client's own token copy, including the refresh token. */
const OIDC_USER_PREFIX = 'oidc.user:';

/** In `sessionStorage` beside what it governs: flag gone ⇒ session gone, so no staleness check. */
export const PUBLIC_SESSION_FLAG = 'myosh.publicSession';

const safely = <T>(read: () => T, fallback: T): T => {
  try {
    return read();
  } catch {
    // Storage access throws (`SecurityError`) when the browser blocks it.
    return fallback;
  }
};

/** Keys the marker cookies by Cognito pool — TEST/OTE/PROD share `*.myosh.com` on different pools. */
export const poolNamespace = (): string => {
  // filter(Boolean) drops a trailing-slash empty segment, or the namespace would be '' (collision).
  const segments = (process.env.REACT_APP_AUTH_URL ?? '').split('/').filter(Boolean);
  const safe = (segments.pop() ?? '').replace(/[^a-zA-Z0-9]/g, '');
  return safe ? `_${safe}` : '';
};

// A durable "last session was public" and a session-scoped "a public session is live now" (no
// Max-Age). Cookies, not localStorage, because Cognito's session is shared across app clients.
const lastPublicCookie = (): string => `myosh_last_public${poolNamespace()}`;
const publicActiveCookie = (): string => `myosh_public_active${poolNamespace()}`;
/** Comfortably outlives Cognito's fixed 60-minute session cookie. */
const LAST_PUBLIC_MAX_AGE_SECONDS = 60 * 60 * 12;

// The broadest domain the browser accepts, so the marker is shared across sibling apps. "Last two
// labels" is wrong on multi-part suffixes (`myosh.com.au` → `com.au`, silently rejected), so with
// no PSL we probe broadest-first and keep the first that sticks. Null for a parentless host.
let resolvedCookieDomain: string | null | undefined;
const cookieDomain = (): string | null => {
  if (resolvedCookieDomain !== undefined) {
    return resolvedCookieDomain;
  }
  resolvedCookieDomain = safely(() => {
    const host = window.location.hostname;
    if (!host.includes('.') || /^[\d.]+$/.test(host)) {
      return null;
    }
    const labels = host.split('.');
    // Unique per call, or a sibling tab's concurrent probe could make a rejected domain look accepted.
    const probe = `__myosh_d_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
    for (let i = labels.length - 2; i >= 0; i--) {
      const candidate = labels.slice(i).join('.');
      // biome-ignore lint/suspicious/noDocumentCookie: sync probe needs the classic API; CookieStore is async and absent in jsdom
      document.cookie = `${probe}=1; path=/; domain=.${candidate}; SameSite=Lax`;
      const accepted = document.cookie.split(';').some((entry) => entry.trim() === `${probe}=1`);
      // biome-ignore lint/suspicious/noDocumentCookie: clean up the probe
      document.cookie = `${probe}=; path=/; domain=.${candidate}; max-age=0; SameSite=Lax`;
      if (accepted) {
        return candidate;
      }
    }
    return null;
  }, null);
  return resolvedCookieDomain;
};

/** `maxAge` null → a session cookie (no `Max-Age`), dropped when the browser closes. */
const writeCookie = (name: string, value: string, maxAge: number | null) => {
  const domain = cookieDomain();
  safely(() => {
    // biome-ignore lint/suspicious/noDocumentCookie: sync writes; CookieStore is async and absent in jsdom
    document.cookie = [
      `${name}=${value}`,
      'path=/',
      domain ? `domain=.${domain}` : '',
      maxAge === null ? '' : `max-age=${maxAge}`,
      'SameSite=Lax',
      window.location.protocol === 'https:' ? 'Secure' : '',
    ]
      .filter(Boolean)
      .join('; ');
  }, undefined);
};

const hasCookie = (name: string): boolean =>
  safely(() => document.cookie.split(';').some((entry) => entry.trim() === `${name}=1`), false);

export const markLastSessionPublic = () => {
  writeCookie(lastPublicCookie(), '1', LAST_PUBLIC_MAX_AGE_SECONDS);
  writeCookie(publicActiveCookie(), '1', null);
};

// Only after the outcome settles (confirmed logout, or an ordinary login), never before the gate's
// redirect — clearing early leaves a failed logout no signal to retry.
export const clearLastSessionPublic = () => {
  writeCookie(lastPublicCookie(), '', 0);
  writeCookie(publicActiveCookie(), '', 0);
};

export const lastSessionWasPublic = (): boolean => hasCookie(lastPublicCookie());

/** Whether a public session is live in some tab of this browser right now (shared cookie). */
export const isPublicSessionLive = (): boolean => hasCookie(publicActiveCookie());

export const isPublicSession = (): boolean => safely(() => sessionStorage.getItem(PUBLIC_SESSION_FLAG) === '1', false);

const writeTarget = (): Storage | null => safely(() => (isPublicSession() ? sessionStorage : localStorage), null);

export const readSessionItem = (key: string): string | null => {
  const scoped = safely(() => sessionStorage.getItem(key), null);
  if (scoped !== null) {
    return scoped;
  }
  // No durable fallback for a public session, or a previous user's leftover (a stale dashboard
  // session id — a credential) would be adopted.
  if (isPublicSession()) {
    return null;
  }
  return safely(() => localStorage.getItem(key), null);
};

/** Writes session material to whichever storage this session belongs in. */
export const writeSessionItem = (key: string, value: string) => {
  const target = writeTarget();
  if (!target) {
    return;
  }
  safely(() => target.setItem(key, value), undefined);
  // Drop any durable namesake so a public session leaves nothing behind, even for
  // keys that predate it.
  if (isPublicSession()) {
    safely(() => localStorage.removeItem(key), undefined);
  }
};

/** Removes session material from both storages, so no copy is left behind. */
export const removeSessionItem = (key: string) => {
  safely(() => sessionStorage.removeItem(key), undefined);
  safely(() => localStorage.removeItem(key), undefined);
};

/** Convenience for the persisted user state, which most consumers read. */
export const readStoredUserState = (): string | null => readSessionItem(USER_STATE_STORAGE_KEY);

const oidcKeys = (store: Storage): string[] =>
  safely(() => Object.keys(store).filter((key) => key.startsWith(OIDC_USER_PREFIX)), []);

/** The dashboard's per-schema session id — a dynamic key, one per schema the user has open. */
const SESSION_ID_PREFIX = 'sessionId-';
/** Fixed app-level session-credential keys outside {@link SESSION_KEYS}. */
const EXTRA_DURABLE_CREDENTIALS = ['dashboardSessionRegistered'];

/**
 * Every durable key holding session material: the fixed set, oidc-client's user (refresh
 * token), the dynamic per-schema dashboard session ids, and the dashboard-registered flag.
 * Enumerated live so dynamic `sessionId-*` keys are covered, not just a fixed list.
 */
const durableCredentialKeys = (store: Storage): string[] =>
  safely(
    () =>
      Object.keys(store).filter(
        (key) =>
          SESSION_KEYS.includes(key) ||
          key.startsWith(OIDC_USER_PREFIX) ||
          key.startsWith(SESSION_ID_PREFIX) ||
          EXTRA_DURABLE_CREDENTIALS.includes(key)
      ),
    []
  );

/** Removes every durable credential (tokens, session ids) — for aborting a failed public login. */
export const purgeDurableCredentials = () => {
  const durable = safely(() => localStorage, null);
  if (!durable) {
    return;
  }
  for (const key of durableCredentialKeys(durable)) {
    safely(() => durable.removeItem(key), undefined);
  }
};

// Switches this session to session-scoped storage, moving what oidc-client already wrote durably
// (incl. the refresh token). If sessionStorage can't be written, stays durable rather than lose it.
export const beginPublicSession = (options?: { migrateDurable?: boolean; markSession?: boolean }) => {
  // Defaults (OIDC path): migrate the tokens oidc-client wrote durably and mark the session. The
  // handoff passes both false — it writes no oidc user and no Cognito session, so it must not
  // adopt stale durable entries nor assert a live public session.
  const migrateDurable = options?.migrateDurable ?? true;
  const markSession = options?.markSession ?? true;

  // This runs twice per login — the OIDC storage adapter before the first token write, then
  // `LoginCallback` from the profile. A second run must not disturb what the first set up.
  const alreadyPublic = safely(() => sessionStorage.getItem(PUBLIC_SESSION_FLAG) === '1', false);

  const flagged = safely(() => {
    // Preserve the first start time: bumping it could push it past a sibling's signedOutAt written
    // between the two calls, so wasSignedOutElsewhere would miss the sign-out. Written before the
    // flag so a failed timestamp write leaves no orphan flag.
    if (sessionStorage.getItem(SESSION_STARTED_AT) === null) {
      sessionStorage.setItem(SESSION_STARTED_AT, String(Date.now()));
    }
    sessionStorage.setItem(PUBLIC_SESSION_FLAG, '1');
    return sessionStorage.getItem(PUBLIC_SESSION_FLAG) === '1' && sessionStorage.getItem(SESSION_STARTED_AT) !== null;
  }, false);

  if (!flagged) {
    // A partial write (one key set, the other not) would route reads to `sessionStorage`
    // while the tokens are still durable, breaking the session. Roll back this call's keys —
    // but only when initialising, so a prior successful call's live session is left intact.
    if (!alreadyPublic) {
      safely(() => {
        sessionStorage.removeItem(PUBLIC_SESSION_FLAG);
        sessionStorage.removeItem(SESSION_STARTED_AT);
      }, undefined);
    }
    return false;
  }

  const durable = safely(() => localStorage, null);
  if (!durable) {
    // Nothing durable to move, but still set the marker (it's a cookie) — else the tokens die
    // on close with Cognito's cookie surviving and no signal to gate the next login.
    if (markSession) {
      markLastSessionPublic();
    }
    return true;
  }

  if (migrateDurable) {
    // Copy and verify all before deleting any: a quota limit could accept the small flag but
    // reject a kilobyte token, and deleting as we go would destroy the only copy.
    const staged: string[] = [];
    const rollBack = () => {
      for (const key of staged) {
        safely(() => sessionStorage.removeItem(key), undefined);
      }
      safely(() => sessionStorage.removeItem(PUBLIC_SESSION_FLAG), undefined);
      safely(() => sessionStorage.removeItem(SESSION_STARTED_AT), undefined);
    };

    for (const key of [...SESSION_KEYS, ...oidcKeys(durable)]) {
      const value = safely(() => durable.getItem(key), null);
      if (value === null) {
        continue;
      }
      const copied = safely(() => {
        sessionStorage.setItem(key, value);
        return sessionStorage.getItem(key) === value;
      }, false);
      if (!copied) {
        rollBack();
        return false;
      }
      staged.push(key);
    }

    for (const key of staged) {
      safely(() => durable.removeItem(key), undefined);
    }
    // A prior user's sessionId-*/dashboardSessionRegistered aren't migrated (adopting them
    // would take over that user's dashboard session) — but they must not outlive this wipe.
    purgeDurableCredentials();
  } else {
    // Token handoff: it writes no oidc user, so drop prior durable credentials instead of
    // migrating them — a leftover oidc user (refresh token) or dashboard session id would be
    // adopted as the previous user.
    for (const key of durableCredentialKeys(durable)) {
      safely(() => durable.removeItem(key), undefined);
    }
  }

  if (markSession) {
    // The marker makes the next browser session clear Cognito first. Skipped for a handoff (no
    // Cognito session to guard) — setting the shared "active" cookie there would suppress a
    // sibling app's gate and let Cognito replay a prior public login.
    markLastSessionPublic();
  }

  return true;
};

/**
 * Returns an ordinary login to durable storage when the tab previously ran a public session
 * (flag survives a login without logout, e.g. a callback-failure retry) — otherwise the
 * ordinary user's tokens stay per-tab and die on close.
 */
export const endPublicSession = () => {
  if (!isPublicSession()) {
    return;
  }
  const scoped = safely(() => sessionStorage, null);
  const durable = safely(() => localStorage, null);
  if (!scoped || !durable) {
    return;
  }
  // Copy and verify all before deleting any — a partial move would strand keys durable
  // where a still-public session cannot read them. A failed copy restores what durable
  // held (a sibling tab's session may own those keys) and leaves this one per-tab, flag kept.
  const staged: Array<[string, string | null]> = [];
  for (const key of [...SESSION_KEYS, ...oidcKeys(scoped)]) {
    const value = safely(() => scoped.getItem(key), null);
    if (value === null) {
      continue;
    }
    const previous = safely(() => durable.getItem(key), null);
    const copied = safely(() => {
      durable.setItem(key, value);
      return durable.getItem(key) === value;
    }, false);
    if (!copied) {
      const toRestore: Array<[string, string | null]> = [...staged, [key, previous]];
      for (const [stagedKey, previousValue] of toRestore) {
        safely(
          () => (previousValue === null ? durable.removeItem(stagedKey) : durable.setItem(stagedKey, previousValue)),
          undefined
        );
      }
      return;
    }
    staged.push([key, previous]);
  }
  for (const [key] of staged) {
    safely(() => scoped.removeItem(key), undefined);
  }
  // The public user's dashboard credentials must not carry into the ordinary session —
  // readSessionItem prefers sessionStorage, and the web login reuses an existing sessionId.
  for (const key of safely(
    () =>
      Object.keys(scoped).filter((key) => key.startsWith(SESSION_ID_PREFIX) || EXTRA_DURABLE_CREDENTIALS.includes(key)),
    [] as string[]
  )) {
    safely(() => scoped.removeItem(key), undefined);
  }
  safely(() => scoped.removeItem(PUBLIC_SESSION_FLAG), undefined);
  // The start time goes with the flag: `wasSignedOutElsewhere` arms on it alone, so leaving
  // it would keep this now-ordinary tab armed for life — any later sign-out anywhere makes
  // the next reload discard a durable session that is perfectly valid.
  safely(() => scoped.removeItem(SESSION_STARTED_AT), undefined);
};

const SIGN_OUT_CHANNEL = 'myosh.session';
const SIGNED_OUT = 'signed-out';

// The broadcast only reaches running tabs; a discarded one misses it, so this durable marker lets
// it find out on reactivation.
const SIGNED_OUT_AT = 'myosh.signedOutAt';
const SESSION_STARTED_AT = 'myosh.sessionStartedAt';

const wipeBothStores = () => {
  safely(() => localStorage.clear(), undefined);
  safely(() => sessionStorage.clear(), undefined);
};

/**
 * Clears the session from both stores here and — via the broadcast — in sibling tabs, whose
 * per-tab `sessionStorage` this can't reach directly.
 */
export const clearStoredSession = () => {
  wipeBothStores();
  // Drop the "live" marker too, or a rejected logout (bowtie/swms don't await it) leaves it
  // suppressing the gate. `myosh_last_public` is kept so the gate retries.
  writeCookie(publicActiveCookie(), '', 0);
  // After the wipe, so it survives.
  safely(() => localStorage.setItem(SIGNED_OUT_AT, String(Date.now())), undefined);
  safely(() => {
    const channel = new BroadcastChannel(SIGN_OUT_CHANNEL);
    channel.postMessage(SIGNED_OUT);
    channel.close();
  }, undefined);
};

// Per-tab session only, after a sibling signed out: the signing-out tab cleared the durable store,
// and wiping it here would delete the sign-out marker a reactivated tab needs. Doesn't re-broadcast.
export const clearStoredSessionLocally = () => {
  safely(() => sessionStorage.clear(), undefined);
};

// Durable store only, for a mount-time cleanup (not a sign-out); leaves per-tab state intact.
export const clearDurableSession = () => {
  // Carry the sign-out marker across the wipe — it's how a discarded tab that missed the
  // broadcast learns on reactivation, and clearing it here would let that tab come back signed in.
  const signedOutAt = safely(() => localStorage.getItem(SIGNED_OUT_AT), null);
  safely(() => localStorage.clear(), undefined);
  if (signedOutAt !== null) {
    safely(() => localStorage.setItem(SIGNED_OUT_AT, signedOutAt), undefined);
  }
};

/** Subscribes this tab to sign-outs from its siblings. Returns an unsubscribe. */
export const onSignedOutElsewhere = (handler: () => void): (() => void) => {
  const channel = safely(() => new BroadcastChannel(SIGN_OUT_CHANNEL), null);
  if (!channel) {
    // Without the channel a sibling tab keeps its own copy until it is closed —
    // no worse than before, and the browser still discards it on close.
    return () => undefined;
  }
  const listener = (event: MessageEvent) => {
    if (event.data === SIGNED_OUT) {
      handler();
    }
  };
  channel.addEventListener('message', listener);
  return () => {
    safely(() => {
      channel.removeEventListener('message', listener);
      channel.close();
    }, undefined);
  };
};

/**
 * Whether a serialized oidc-client user carries the public-computer claim. The library stores the
 * decoded profile alongside the tokens, so this needs no JWT handling unless the profile is missing.
 */
const serializedUserIsPublicComputer = (value: string): boolean => {
  try {
    return isPublicComputerUser(JSON.parse(value));
  } catch {
    return false;
  }
};

// A `Storage` view for oidc-client's `WebStorageStateStore`. Recognising a public session on its
// first write (not later, when our code sees the claim) avoids a crash window that strands a token.
export const sessionScopedStore: Storage = {
  get length() {
    return safely(() => (writeTarget() ?? localStorage).length, 0);
  },
  key: (index: number) => safely(() => (writeTarget() ?? localStorage).key(index), null),
  getItem: (key: string) => readSessionItem(key),
  setItem: (key: string, value: string) => {
    if (!isPublicSession() && key.startsWith(OIDC_USER_PREFIX) && serializedUserIsPublicComputer(value)) {
      // Fail closed: if the session can't be isolated, drop the write — persisting the token
      // durably is the leak "don't save details" must prevent.
      if (!beginPublicSession()) {
        return;
      }
    }
    writeSessionItem(key, value);
  },
  removeItem: (key: string) => removeSessionItem(key),
  clear: () => clearStoredSession(),
};

/**
 * Whether this tab was signed out while not running. Checked at startup — a discarded tab
 * reloads on reactivation, so this runs before anything reads the restored session.
 */
export const wasSignedOutElsewhere = (): boolean => {
  const startedAt = Number(safely(() => sessionStorage.getItem(SESSION_STARTED_AT), null));
  if (!Number.isFinite(startedAt) || startedAt === 0) {
    return false;
  }
  const signedOutAt = Number(safely(() => localStorage.getItem(SIGNED_OUT_AT), null));
  return Number.isFinite(signedOutAt) && signedOutAt > startedAt;
};
